import React, { useRef, useEffect, useState, useCallback } from 'react';
import { MapPin } from 'lucide-react';

// Google Maps API key from environment variable
const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_PLACES_API_KEY;

// Load Google Maps API dynamically
let googleMapsPromise = null;
const loadGoogleMapsAPI = () => {
  if (googleMapsPromise) return googleMapsPromise;
  
  if (window.google?.maps?.importLibrary) {
    return Promise.resolve();
  }

  googleMapsPromise = new Promise((resolve, reject) => {
    if (!GOOGLE_API_KEY) {
      reject(new Error('Google Places API key not configured'));
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}&libraries=places&v=weekly`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Maps API'));
    document.head.appendChild(script);
  });

  return googleMapsPromise;
};

/**
 * Google Places Autocomplete Component
 * Restricted to Canadian addresses only
 * Extracts only street address (number + street name) for the input field
 */
const AddressAutocomplete = ({ 
  value, 
  onChange, 
  onAddressSelect,
  placeholder = "Start typing your address...",
  className = "",
  disabled = false
}) => {
  const containerRef = useRef(null);
  const autocompleteRef = useRef(null);
  const initialized = useRef(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [localValue, setLocalValue] = useState(value || '');
  
  const onChangeRef = useRef(onChange);
  const onAddressSelectRef = useRef(onAddressSelect);
  
  useEffect(() => {
    onChangeRef.current = onChange;
    onAddressSelectRef.current = onAddressSelect;
  }, [onChange, onAddressSelect]);

  useEffect(() => {
    setLocalValue(value || '');
  }, [value]);

  const parseAddressComponents = useCallback((addressComponents) => {
    const result = {
      streetNumber: '',
      streetName: '',
      city: '',
      province: '',
      postalCode: ''
    };

    if (!addressComponents) return result;

    for (const component of addressComponents) {
      const types = component.types || [];
      const text = component.longText || component.shortText || '';
      const shortText = component.shortText || '';
      
      if (types.includes('street_number')) {
        result.streetNumber = text;
      }
      if (types.includes('route')) {
        result.streetName = text;
      }
      if (types.includes('locality')) {
        result.city = text;
      } else if (!result.city && types.includes('sublocality_level_1')) {
        result.city = text;
      } else if (!result.city && types.includes('administrative_area_level_2')) {
        result.city = text;
      }
      if (types.includes('administrative_area_level_1')) {
        result.province = shortText;
      }
      if (types.includes('postal_code')) {
        result.postalCode = text;
      }
    }

    return result;
  }, []);

  useEffect(() => {
    if (initialized.current || !containerRef.current) return;

    let mounted = true;

    const init = async () => {
      try {
        // Load Google Maps API from environment variable
        await loadGoogleMapsAPI();
        
        if (!mounted || !containerRef.current || initialized.current) return;
        
        // Import Places library
        const { PlaceAutocompleteElement } = await window.google.maps.importLibrary('places');
        
        if (!mounted || !containerRef.current || initialized.current) return;

        // Create autocomplete element with CANADA ONLY restriction
        const autocomplete = new PlaceAutocompleteElement({
          componentRestrictions: { country: ['ca'] },
          types: ['address']
        });

        autocompleteRef.current = autocomplete;

        // Handle place selection
        autocomplete.addEventListener('gmp-select', async (event) => {
          const placePrediction = event.placePrediction;
          if (!placePrediction) return;

          try {
            const place = placePrediction.toPlace();
            await place.fetchFields({
              fields: ['addressComponents', 'formattedAddress']
            });

            const parsed = parseAddressComponents(place.addressComponents);
            
            // Build street address (number + street name only)
            const streetAddress = [parsed.streetNumber, parsed.streetName]
              .filter(Boolean)
              .join(' ');

            // Update input to show ONLY street address
            const input = autocomplete.querySelector('input');
            if (input) {
              requestAnimationFrame(() => {
                input.value = streetAddress;
                input.dispatchEvent(new Event('input', { bubbles: true }));
              });
            }

            if (onChangeRef.current) {
              onChangeRef.current(streetAddress);
            }

            if (onAddressSelectRef.current) {
              onAddressSelectRef.current({
                streetAddress: streetAddress,
                unit: '',
                city: parsed.city,
                province: parsed.province,
                postalCode: parsed.postalCode,
                formattedAddress: place.formattedAddress
              });
            }
          } catch (err) {
            console.error('Error processing place selection:', err);
          }
        });

        containerRef.current.appendChild(autocomplete);
        initialized.current = true;
        
        if (mounted) {
          setIsLoaded(true);
        }
      } catch (err) {
        console.error('Failed to initialize Places Autocomplete:', err);
      }
    };

    init();

    return () => {
      mounted = false;
    };
  }, [parseAddressComponents]);

  const handleFallbackChange = (e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    if (onChangeRef.current) {
      onChangeRef.current(newValue);
    }
  };

  return (
    <div className="relative">
      {!isLoaded && (
        <input
          type="text"
          value={localValue}
          onChange={handleFallbackChange}
          placeholder={placeholder}
          disabled={disabled}
          className={`input-clean pr-10 ${className}`}
          autoComplete="off"
          data-testid="address-autocomplete-input"
        />
      )}
      
      <div 
        ref={containerRef} 
        className={`google-places-container ${isLoaded ? '' : 'hidden'}`}
        data-testid="address-autocomplete-container"
      />
      
      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none z-10">
        <MapPin className={`w-4 h-4 ${isLoaded ? 'text-[var(--brand-green)]' : 'text-[var(--text-light)]'}`} />
      </div>
    </div>
  );
};

export default AddressAutocomplete;
